import { useEffect, useState } from "react";
import { Youtube } from "lucide-react";
import api from "../lib/api";

export function CuratedTechShorts() {
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    let active = true;
    api.get("/curated-shorts").then(async (r) => {
      const list = (r.data.videos || []).map((v) => ({
        id: v.id, title: v.title || "", author: v.channel || "", authorUrl: "", date: v.published || "", available: true,
      }));
      // For any item missing a title (e.g. the fallback lineup), enrich + availability-check via oEmbed.
      const enriched = await Promise.all(list.map(async (v) => {
        if (v.title) return v;
        try {
          const res = await fetch(`https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(`https://www.youtube.com/watch?v=${v.id}`)}`);
          if (!res.ok) return { ...v, available: false };
          const d = await res.json();
          return { ...v, title: d.title || "", author: d.author_name || "", authorUrl: d.author_url || "" };
        } catch (_e) { return v; } // fail open
      }));
      if (active) setVideos(enriched.filter((v) => v.available));
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  if (videos.length === 0) return null;

  return (
    <section className="mt-16" data-testid="curated-tech-shorts">
      <div className="border-b-2 border-pti-black pb-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-6 h-6 bg-pti-red text-white"><Youtube size={15} /></span>
          <h2 className="font-display text-2xl sm:text-3xl font-bold">Curated Tech Shorts</h2>
        </div>
        <p className="text-sm text-neutral-500 mt-1">Technology videos selected by PictureThisInk</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-6">
        {videos.slice(0, 4).map((v) => (
          <div key={v.id} data-testid={`curated-short-${v.id}`} className="group border border-neutral-200 bg-white hover:border-pti-red transition-colors duration-300">
            <div className="relative w-full overflow-hidden bg-black" style={{ aspectRatio: "16 / 9" }}>
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${v.id}`}
                title={v.title || "PictureThisInk curated tech short"}
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
            <div className="p-3">
              {v.title && <p className="text-sm font-semibold leading-tight line-clamp-2 text-pti-black">{v.title}</p>}
              <div className="mt-1.5 flex items-center justify-between gap-2">
                <a
                  href={v.authorUrl || `https://www.youtube.com/watch?v=${v.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`curated-short-credit-${v.id}`}
                  className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-pti-red hover:underline"
                >
                  <Youtube size={12} /> {v.author ? `via YouTube · ${v.author}` : "Watch on YouTube"}
                </a>
                {v.date && <span className="text-[11px] text-neutral-400 shrink-0">{new Date(v.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
